import classNames from 'classnames';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import { Alert, Button, ButtonGroup } from '@trussworks/react-uswds';
import './routeError.scss';

type RouteErrorValue = ErrorComponentProps['error'];

const pageStyles = classNames('route-error-page', 'grid-container', 'margin-y-5');
const panelStyles = classNames(
  'route-error-page__panel',
  'bg-white',
  'border',
  'border-base-lighter',
  'radius-md',
  'shadow-2',
  'padding-4'
);
const preStyles = classNames('route-error-page__pre', 'bg-base-lightest', 'padding-2');
const getErrorMessage = (error: RouteErrorValue) => {
  return error instanceof Error && error.message ? error.message : 'An unexpected error occurred.';
};

const getErrorStack = (error: RouteErrorValue) => {
  return error instanceof Error ? error.stack : undefined;
};

const RouteError = ({ error, info, reset }: ErrorComponentProps) => {
  const navigate = useNavigate();

  const errorMessage = getErrorMessage(error);
  const errorStack = getErrorStack(error);

  const handleReturnHome = () => {
    reset();
    void navigate({ to: '/home' });
  };

  return (
    <main id='main-content' className={pageStyles}>
      <div className='grid-row flex-justify-center'>
        <div className='grid-col-12 desktop:grid-col-10 widescreen:grid-col-8'>
          <section className={panelStyles}>
            <Alert type='error' heading='This page could not be loaded' headingLevel='h1' slim>
              Try again or return to the homepage. If the problem persists, contact support for assistance.
            </Alert>

            <ButtonGroup className='margin-top-3'>
              <Button type='button' onClick={reset}>
                Try again
              </Button>
              <Button type='button' outline onClick={handleReturnHome}>
                Return home
              </Button>
            </ButtonGroup>

            <div className='border-top border-base-lighter margin-top-4 padding-top-3'>
              <p className='text-bold margin-bottom-1'>Error details</p>
              <p className='text-base margin-top-0'>
                The following information can help our team investigate the issue.
              </p>
              <details className='margin-top-2'>
                <summary className='text-bold'>View technical details</summary>
                <p className='text-bold margin-top-3 margin-bottom-1'>Message</p>
                <pre className={preStyles}>{errorMessage}</pre>
                {errorStack && (
                  <>
                    <p className='text-bold margin-top-3 margin-bottom-1'>Stack trace</p>
                    <pre className={preStyles}>{errorStack}</pre>
                  </>
                )}
                {info?.componentStack && (
                  <>
                    <p className='text-bold margin-top-3 margin-bottom-1'>Component stack</p>
                    <pre className={preStyles}>{info.componentStack}</pre>
                  </>
                )}
              </details>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default RouteError;
